<div class="container-fluid payment-mapping-page" data-test="payment-mapping-page">
	<p-toast [style]="{marginTop: '80px'}"></p-toast>

	@if (formLoading) {
		<div class="progress-spinner">
			<app-spinner [formLoading]="formLoading"></app-spinner>
		</div>
	}

	<!-- Page header -->
	<div class="page-header d-flex justify-content-between align-items-center">
		<div>
			<h2 data-test="payment-mapping-title">Payment Reconciliation — 4-Way Mapping</h2>
			<div class="page-subtitle">
				Per-payment trace: Wiki/Percona &rarr; Auth.net batch &rarr; Bank MERCH DEP &rarr; QuickBooks JE
			</div>
		</div>
		<div class="d-flex gap-2 align-items-end">
			<!-- Run date picker -->
			<div class="form-group as-of-picker">
				<label for="runDate">Payment date</label>
				<p-calendar inputId="runDate"
					[(ngModel)]="runDate"
					dateFormat="mm/dd/yy"
					[showIcon]="true"
					(onSelect)="onRunDateChange()"
					[appendTo]="'body'"
					data-test="pm-rundate-picker">
				</p-calendar>
			</div>

			<!-- Location selector -->
			<div class="form-group">
				<label for="pmLocation">Location</label>
				<p-dropdown inputId="pmLocation"
					[options]="locationOptions"
					[(ngModel)]="location"
					(onChange)="onLocationChange()"
					data-test="pm-location-filter">
				</p-dropdown>
			</div>

			<button pButton type="button"
				label="Refresh"
				icon="pi pi-refresh"
				class="p-button-text"
				(click)="loadMappings()"
				data-test="pm-refresh-btn">
			</button>

			<button pButton type="button"
				label="Back to Escrow Report"
				icon="pi pi-arrow-left"
				class="p-button-secondary"
				(click)="gotoMain()"
				data-test="pm-back-btn">
			</button>
		</div>
	</div>

	<!-- Summary tiles -->
	<div class="pm-summary-strip" data-test="pm-summary-strip">
		<div class="pm-tile pm-tile-total" data-test="pm-tile-total">
			<div class="pm-tile-label">Total payments</div>
			<div class="pm-tile-value">{{ totalRows }}</div>
		</div>
		<div class="pm-tile pm-tile-matched" data-test="pm-tile-matched">
			<div class="pm-tile-label">All legs matched</div>
			<div class="pm-tile-value">{{ matchedRows }}</div>
		</div>
		<div class="pm-tile pm-tile-discrepancy" data-test="pm-tile-discrepancy">
			<div class="pm-tile-label">Discrepancies</div>
			<div class="pm-tile-value">{{ discrepancyRows }}</div>
		</div>
		<div class="pm-tile pm-tile-rate" data-test="pm-tile-rate">
			<div class="pm-tile-label">Match rate</div>
			<div class="pm-tile-value">{{ matchRate }}</div>
		</div>
	</div>

	<!-- Reservation filter banner (shown when cross-linked from escrow report) -->
	@if (filterReservationId) {
		<div class="res-filter-banner" data-test="pm-res-filter-banner">
			<i class="pi pi-filter-fill"></i>
			Showing payments for reservation #{{ filterReservationId }} only
			<button pButton type="button" icon="pi pi-times" label="Show all dates"
				class="p-button-text p-button-sm ms-2"
				(click)="clearReservationFilter()"
				data-test="pm-clear-res-filter">
			</button>
		</div>
	}

	<!-- Filter bar + table -->
	<div class="pm-table-section" data-test="pm-table-section">
		<div class="d-flex justify-content-between align-items-center filters-row">
			<h3>Payment Mapping Detail</h3>
			<div class="d-flex gap-2 filter-controls align-items-center">
				<p-dropdown [options]="discrepancyOptions"
					[(ngModel)]="discrepancyFilter"
					(onChange)="applyFilters()"
					placeholder="Filter by status"
					data-test="pm-discrepancy-filter">
				</p-dropdown>
				<span class="row-count" data-test="pm-row-count">
					{{ filteredRows?.length || 0 }} / {{ rows?.length || 0 }} rows
				</span>
			</div>
		</div>

		<p-table #mappingTable
			[value]="filteredRows"
			[paginator]="true"
			[rows]="50"
			[rowsPerPageOptions]="[25,50,100,250]"
			[showCurrentPageReport]="true"
			currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
			[globalFilterFields]="['paymentConfirmId','reservationId','authnetTxnId','authnetBatchId','qboJournalEntryId']"
			styleClass="p-datatable-sm pm-table"
			dataKey="mappingId"
			data-test="pm-table">

			<ng-template pTemplate="caption">
				<div class="table-caption-row">
					<span class="p-input-icon-left">
						<i class="pi pi-search"></i>
						<input pInputText type="text" placeholder="Search txn ID, res ID, batch..."
							(input)="mappingTable.filterGlobal($any($event.target).value, 'contains')"
							data-test="pm-search">
					</span>
				</div>
			</ng-template>

			<ng-template pTemplate="header">
				<tr>
					<!-- Status -->
					<th style="width: 6rem">Status</th>

					<!-- Leg 1: Percona / Wiki -->
					<th class="leg-header leg-percona" colspan="3">
						<span class="leg-label">① Percona (Wiki)</span>
					</th>

					<!-- Leg 2: Auth.net -->
					<th class="leg-header leg-authnet" colspan="3">
						<span class="leg-label">② Auth.net</span>
					</th>

					<!-- Leg 3: Bank -->
					<th class="leg-header leg-bank" colspan="3">
						<span class="leg-label">③ Bank MERCH DEP</span>
					</th>

					<!-- Leg 4: QBO -->
					<th class="leg-header leg-qbo" colspan="3">
						<span class="leg-label">④ QuickBooks JE</span>
					</th>
				</tr>
				<tr>
					<!-- Status col -->
					<th pSortableColumn="discrepancyType">
						Code <p-sortIcon field="discrepancyType"></p-sortIcon>
					</th>

					<!-- Percona leg sub-headers -->
					<th pSortableColumn="paymentConfirmId" class="leg-percona">
						Confirm ID <p-sortIcon field="paymentConfirmId"></p-sortIcon>
					</th>
					<th pSortableColumn="reservationId" class="leg-percona">
						Res ID <p-sortIcon field="reservationId"></p-sortIcon>
					</th>
					<th pSortableColumn="perconaAmount" class="leg-percona text-right">
						Amount <p-sortIcon field="perconaAmount"></p-sortIcon>
					</th>

					<!-- Auth.net leg sub-headers -->
					<th pSortableColumn="authnetTxnId" class="leg-authnet">
						Txn ID <p-sortIcon field="authnetTxnId"></p-sortIcon>
					</th>
					<th pSortableColumn="authnetBatchId" class="leg-authnet">
						Batch ID <p-sortIcon field="authnetBatchId"></p-sortIcon>
					</th>
					<th pSortableColumn="authnetAmount" class="leg-authnet text-right">
						Settled <p-sortIcon field="authnetAmount"></p-sortIcon>
					</th>

					<!-- Bank leg sub-headers -->
					<th pSortableColumn="bankDepositDate" class="leg-bank">
						Deposit Date <p-sortIcon field="bankDepositDate"></p-sortIcon>
					</th>
					<th pSortableColumn="bankAccount" class="leg-bank">
						Account <p-sortIcon field="bankAccount"></p-sortIcon>
					</th>
					<th pSortableColumn="orderShareAmount" class="leg-bank text-right">
						Share <p-sortIcon field="orderShareAmount"></p-sortIcon>
					</th>

					<!-- QBO leg sub-headers -->
					<th pSortableColumn="qboJournalEntryId" class="leg-qbo">
						JE ID <p-sortIcon field="qboJournalEntryId"></p-sortIcon>
					</th>
					<th pSortableColumn="qboSyncLogId" class="leg-qbo">
						Sync Log <p-sortIcon field="qboSyncLogId"></p-sortIcon>
					</th>
					<th pSortableColumn="qboAmount" class="leg-qbo text-right">
						QBO Amt <p-sortIcon field="qboAmount"></p-sortIcon>
					</th>
				</tr>
			</ng-template>

			<ng-template pTemplate="body" let-row>
				<tr [class]="rowStatusClass(row)" [attr.data-test-row]="row.mappingId">

					<!-- Status cell -->
					<td>
						@if (row.allLegsMatch) {
							<span class="match-pill" data-test="pm-match-pill">✓ Match</span>
						} @else {
							<span [class]="discrepancyClass(row.discrepancyType)"
								[title]="row.discrepancyDelta != null ? ('Delta: ' + formatCurrency(row.discrepancyDelta)) : ''"
								data-test="pm-disc-pill">
								{{ discrepancyLabel(row.discrepancyType) }}
							</span>
						}
					</td>

					<!-- Percona leg -->
					<td class="leg-percona mono">{{ row.paymentConfirmId }}</td>
					<td class="leg-percona mono">
						<a class="res-link" title="View in Escrow Report"
							(click)="gotoEscrowReport(row.reservationId)"
							data-test="pm-res-link">{{ row.reservationId }}</a>
					</td>
					<td class="leg-percona text-right amount">
						<strong>{{ formatCurrency(row.perconaAmount) }}</strong>
					</td>

					<!-- Auth.net leg -->
					<td class="leg-authnet">
						<span [class]="legClass(!!row.authnetTxnId)" data-test="pm-authnet-txn">
							{{ row.authnetTxnId || '—' }}
						</span>
					</td>
					<td class="leg-authnet mono">{{ row.authnetBatchId || '—' }}</td>
					<td class="leg-authnet text-right amount">
						{{ formatCurrency(row.authnetAmount) }}
					</td>

					<!-- Bank leg -->
					<td class="leg-bank">
						<span [class]="legClass(!!row.bankTxnId)" data-test="pm-bank-date">
							{{ row.bankDepositDate ? formatDate(row.bankDepositDate) : '—' }}
						</span>
					</td>
					<td class="leg-bank">{{ row.bankAccount || '—' }}</td>
					<td class="leg-bank text-right amount">
						{{ formatCurrency(row.orderShareAmount) }}
					</td>

					<!-- QBO leg -->
					<td class="leg-qbo">
						<span [class]="legClass(!!row.qboSyncLogId)" data-test="pm-qbo-je">
							{{ row.qboJournalEntryId || '—' }}
						</span>
					</td>
					<td class="leg-qbo mono">{{ row.qboSyncLogId || '—' }}</td>
					<td class="leg-qbo text-right amount">
						{{ formatCurrency(row.qboAmount) }}
					</td>
				</tr>
			</ng-template>

			<ng-template pTemplate="emptymessage">
				<tr>
					<td colspan="13" class="text-center empty-row" data-test="pm-empty">
						@if (formLoading) {
							Loading...
						} @else {
							No mapping rows found for {{ formatDate(runDate) }} ({{ location }}). The nightly job
							runs at 7:00 AM EST — check back after the job has run for this date.
						}
					</td>
				</tr>
			</ng-template>
		</p-table>
	</div>
</div>
